import _ from "lodash";
import { Button, ComponentOptions, TemplateComponent } from "../../../app/core/ui";
import * as util from "../../../app/core/util/util";
import { codes, controls, dataSource, deepClone, getAll, settings, userHasRight } from "../../../framework";
import { RightLockable } from "../../components/components/component";
import * as maskUtil from "../../components/mask";
import * as divisionRegion from "../../core/division.region";
import * as zKendo from "../../core/kendo";
import { ll } from "../../core/ll";
import { user } from "../../core/user";
import { emptyFn } from "../../core/util";
import { buildODataKendoParams } from "../../data/buildODataKendoParams";
import * as filter from "../../data/filter";
import * as odata from "../../data/odata";
import * as schema from "../../data/schema";
import { searchTypes } from "../../data/searchTypes";
import { deleteEntity } from "../deleteEntity";
import { fixGridFilterMenuForChrome, GridFilterMenu } from "../gridFilterMenu";
import * as gridUtil from "../gridUtil";
import * as searchOptions from "../searchOptions";
import K_Grid from "../templates/K_Grid";
import * as gridColumns from "./grid/gridColumns";
import { SignaturePopup } from "./signaturePopup";
import { ServiceProEntity } from "@msidata/service-pro-api-schema";
import { getGridStickyDefaults, setGridStickyDefaults } from "../../../app/core/util/stickyDefaults";

export interface GridOptions<T = void> extends ComponentOptions {
    containerId?: string;
    searchContainerId?: string;
    searchClasses?: string;
    bsc?: string;
    label?: string;
    /**
     * The entity that the table will use to generate columns based upon the schema.
     */
    entity?: T extends ServiceProEntity ? T["entityName"] : string;
    /**
     * This specifies columns to append to the OData select parameter in addition to the columns that are visible in the grid.
     */
    alwaysSelect?: string[];
    autoBind?: boolean;
    /**
     * Specifies the column order and generates the columns from the fields specified.
     */
    columnOrder?: T extends ServiceProEntity ? (EntityFields<T>)[] : string[];
    /**
     * Overrides columns based on the 'field' attribute.
     */
    columnOverrides?: GridColumn<T>[];
    /**
     * Specifies the OData expands for the datasource.
     */
    expand?: T extends ServiceProEntity ? (EntityFields<T> | "*")[] : string[];
    /**
     * Event handler for addition of grid columns
     */
    columnShow?: () => void;
    /**
     * Event handler for removal of grid columns
     */
    columnHide?: () => void;
    /**
     * Used to explicitly set the height of the grid.
     */
    height?: number | string;
    editButton?: boolean;
    editable?: boolean | string;
    deleteButton?: boolean | { checkDelete: boolean };
    liveFilter?: boolean;
    /**
     * Specifies columns that do not need to follow our schema and can use custom data.
     */
    utilityColumns?: (GridColumn & { localize?: boolean, exportHeaderAttributes?: any })[];
    /**
     * Specifies what columns should be available. All based upon the schema are available by default.
     */
    availableColumns?: T extends ServiceProEntity ? (EntityFields<T> | "*")[] : string[];
    /**
     * Uses the default kendo grid columns.
     * You do not need to specify an entity when this is used.
     * Most other column related attributes are ignored when using this.
     */
    gridColumns?: any[];
    stickyDefaultName?: string;
    /**
     * Specifies the max amount of rows to be displayed on a page.
     */
    pageSize?: number;
    kendoSort?: any;
    widgetId?: string;
    autoSelect?: boolean;
    fireChangeOnReselect?: boolean;
    fireChangeOnReselectIgnoreNull?: boolean;
    allowExcelExport?: boolean;
    /**
     * Specifies the OData filter for the datasource.
     */
    filter?: any;
    /**
     * Specifies the search options for the grid.
     */
    searchOptions?: any[];
    searchOptionsFilter?: any;
    beforeSend?: any;
    /**
     * Specifies how the data will be sorted in the grid by default.
     */
    sort?: string;
    editOnDoubleClick?: boolean;
    urlPrefix?: string;
    urlPostfix?: string;
    /**
     * The url for the datasource.
     */
    url?: string;
    companyGuid?: string;
    detailInit?: any;
    detailTemplate?: any;
    divisions?: boolean;
    regions?: boolean;
    isSearchPopUp?: boolean;
    dataSource?: any;
    pageable?: any;
    /**
     * The HTML object to be used to generate the grid.
     * This is used for when you would want to generate from an HTML object instead of an Id.
     */
    gridObject?: any;
    regionsFilterOverride?: any;
    /**
     * A function to be called whenever data is bound to the grid.
     */
    dataBound?: any;
    /**
     * A function to be called whenever a row is select on the grid.
     */
    change?: any;
    /**
     * Determines whether or not rows can be selected in the grid.
     */
    selectable?: boolean | string;
    hideSearchOptions?: boolean;
    /**
     * If true or undefined, the default height is used.
     * If false, the height will be determined by the number of rows on the grid.
     */
    defaultHeight?: boolean;
    pushColumns?: boolean;
    filterable?: boolean | object;
    sortable?: boolean | object;
    searchType?: any;
    divisionsNavigationTypes?: any;
    regionsNavigationTypes?: any;
    divisionsFilterOverride?: any;
    regionNotRequired?: any;
    divisionsAndRegionsAndMyStuff?: any;
    defaultDivisionSearchOption?: any;
    defaultRegionSearchOption?: any;
    /**
     * Renames the search option along with the related column title
     */
    columnRenames?: T extends ServiceProEntity ? { [K in keyof T]?: string } : { [key: string]: string };
    clearAllFiltersOption?: boolean;
    ignoreExpandWarning?: boolean;
    refresh?: boolean;
    staticDataSource?: boolean;
    kendoFilter?: any;
    displayFirstItemInCollections?: string[];
    requiredRight?: string;
    /**
     * This specifies columns to append to the OData expand parameter in addition to the ones that are included in the expand option.
     * This is used if you want to add an OData expand without the grid generating selectable columns for the entity in that expand.
     */
    alwaysExpand?: string[];
    defaultColumnWidth?: number;
    databaseId?: number;
    isUserGridTab?: boolean;
    allowColumnSelection?: boolean;
    /**
     * A function to be called whenever a column is reordered in the grid grid.
     */
    columnReorder?: any;
    persistSelection?: boolean;
}

type EntityFields<T> = T extends ServiceProEntity ? Exclude<keyof T, "entityName"> : string;
export interface GridColumn<T = void> {
    field?: EntityFields<T>;
    template?: string;
    title?: string;
    width?: number | string;
    headerTemplate?: string;
    selectPath?: string;
    sortable?: boolean | { compare: ((a: any, b: any) => number) };
    command?: GridColumnCommand[];
    hidden?: boolean;
    attributes?: any;
    filterable?: boolean;
    hideColumnMenu?: boolean;
    editable?: boolean;
    editor?: any;
    localize?: boolean;
}

export interface GridColumnCommand {
    name: string;
    text: string;
    className: string;
    iconClass?: string;
}

export class Grid<T = void> extends TemplateComponent {

    options: GridOptions & RightLockable;

    kendoGrid: kendo.ui.Grid;
    selectedItem: any;
    parameterMap: any;
    dontUpdateStickyDefaults: boolean;
    customFilterMenus: any[];

    // tslint:disable:variable-name
    _previousPageSize: any;
    _previousSort: any;
    _autoSelect: boolean;
    _fireChangeOnReselect: boolean;
    _fireChangeOnReselectIgnoreNull: boolean;
    _onChange: (({ selectedItem }) => void)[];
    _onDoubleClick: (({ item }) => void)[];
    _onDelete: (({ item }) => void)[];
    _onEdit: (({ item }) => void)[];
    _onDataBound: (({ selectedItem }) => void)[];
    _canDelete: (data: any) => boolean;
    _urlDataSource: boolean;

    constructor(options: GridOptions<T>) {
        super(K_Grid, options);

        this.customFilterMenus = [];
    }

    preRender() {
        const options = this.options;

        options.containerId = options.containerId || `${this.id}_Container`;
        options.searchContainerId = options.searchContainerId || `${this.id}_SearchContainer`;

        if (options.requiredRight) {
            options.rightLocked = !userHasRight(options.requiredRight);

            if (options.rightLocked) {
                options.deleteButton = false;
                options.classes += " right-locked";

                // edit button can still be shown so that the user can open a popup to see more detail
                // restricting edit is then the responsibility of the popup
                // if the edit icon needs to be hidden it needs to be hidden in the gridOptions
            }
        } else {
            options.rightLocked = false;
        }

        // If options.entity is not present do not initialize the grid the new way. Instead continue to use z.controls.grid.
        if (options.entity) {
            this.setOptions();
        }
    }

    create() {
        var me = this;

        // If options.entity is not present do not initialize the grid the new way. Instead continue to use z.controls.grid.
        if (me.options.entity) {
            me.build();
        }
    }

    setOptions(options?: GridOptions & RightLockable) {
        var me = this;

        options = (_.assign(me.options, options));

        me.id = me._getGridIdFromOptions(options);

        // Properties to always include in the OData select (e.g. ["*", "AssignedTech/ServiceTechNumber", "AssignedTech/ServiceTechName"])
        // You would use this if you need to display some field values of the selected item beneath the grid.
        // You can also use this if you use the selected item to display an edit screen, but a better practice would be to use the Id
        // of the selected item to do a new OData query. That way the grid only has to select the visible columns instead of doing a select *.
        options.alwaysSelect = options.alwaysSelect || [];

        // If set to false, will not automatically show data in the grid.
        // You might want to do this if you need to set a filter first and then refresh the grid to show the data.
        options.autoBind = options.autoBind === undefined
            ? true
            : options.autoBind;

        options.defaultHeight = options.defaultHeight === undefined
            ? true
            : options.defaultHeight;

        if (options.defaultHeight) {
            // Height of the grid. Defaults to 349 because that is the height necessary for a grid with 10 items.
            // When the pencil or trash can icons are visible, the selected row ends up taking up an extra pixel.
            options.height = options.height || ((options.editButton || options.deleteButton) ? 350 : 349);
        }

        // If there are search options and this is set to false, the user will have to click the Search button before the grid is refiltered.
        // Otherwise it refilters whenever a search option value changes.
        options.liveFilter = options.liveFilter === undefined
            ? true
            : options.liveFilter;

        // An array of extra columns that should be added that do no correspond to a particular field from the data.
        // These columns will appear after all other columns, visible or hidden.
        options.utilityColumns = options.utilityColumns || [];

        options.searchType = options.searchType || searchTypes.generate(options.entity);

        // An array of navigation properties to expand (e.g. ["OrderSegment", "OrderSegment/OrderLine", "Site", "Appointment"])
        options.expand = _.union(options.expand || [], (options.searchType.expand || "").split(/[ ,]+/).filter(Boolean));

        // Check if expand contains any codes if in dev mode and not using a custom datasource.
        if (settings.showDebugMessages && !options.ignoreExpandWarning) {
            var codeExpands = [];

            _.each(options.expand, (expand) => {
                if (codes[expand]) {
                    codeExpands.push(expand);
                }
            });

            if (codeExpands.length > 0) {
                controls.alert({
                    type: "warning",
                    header: "Grid Expand Contains Codes",
                    message: `Grid with Id of ${me.id} contains one or more expands for ${codeExpands.join(", ")}.  Use the local cache instead.`,
                    fade: false
                });
            }
        }

        options.columnRenames = _.assign(options.searchType.columnRenames || {}, options.columnRenames || {});

        // If options.editButton or options.deleteButton are set, add a utility column with the appropriate icon(s).
        if (options.editButton || options.deleteButton) {
            var editDeleteColumn = {
                command: [] as GridColumnCommand[],
                width: 0,
                id: "EditDelete"
            };

            if (options.editButton) {
                editDeleteColumn.command.push(options.rightLocked ? {
                    name: "view",
                    className: 'k-grid-search',
                    text: "",
                    iconClass: 'k-icon k-i-search'
                } : {
                    className: "k-grid-edit",
                    name: "edit",
                    text: ""
                });

                options.editable = false;
            }

            if (options.deleteButton) {
                editDeleteColumn.command.push({
                    className: "k-grid-delete",
                    name: "destroy",
                    text: ""
                });
            }

            editDeleteColumn.width = editDeleteColumn.command.length * 32;

            if (options.gridColumns) {
                options.gridColumns.push(editDeleteColumn);
            } else {
                options.utilityColumns.push(editDeleteColumn);
            }
        }

        if (!options.gridColumns) {
            // An array of the properties that are visible by default, in the order that they should be shown.
            options.columnOrder = options.columnOrder || schema.getDisplayFields({ entityName: options.entity });

            options.pushColumns = options.pushColumns === undefined
                ? (options.columnOrder.length == 0)
                : options.pushColumns;

            // options.columnOverrides lets you override details for a specific column, such as the template or the title.
            //
            // "columns": [
            //     {
            //         "field": "TaxPercent",
            //         "template": "#= data.TaxPercent # %"
            //     }
            // ]
            //
            // "columns": [
            //     {
            //         "field": "AppointmentStatus/Value",
            //         "title": "Description"
            //     }
            // ]
            //
            // Note when changing the title that this refers to a localized language entry, so the code will call z.ll("Description") for you.
            // If you want to change the title of a column for a specific entity EVERYWHERE, you can add a localized language entry for "EntityName.ColumnName".
            // e.g. If I added a localized language entry with name "AppointmentStatus.Value" and a value of "Description" I would not have to specify
            // anything in the json because it would be taken care of automatically in the grid.
            options.columnOverrides = options.columnOverrides || [];
            // options.availableColumns is an array of the columns the user has the option to show.
            // If this is not provided, they will be able to choose to show any of the columns from the entity or any of the expands.
            if (!options.availableColumns) {
                options.availableColumns = _.map(options.expand, function (expand) {
                    return schema.combinePaths(expand, "*");
                });
                options.availableColumns.push("*");
            }

            options.availableColumns = schema.getAvailableSelectPaths({
                entityName: options.entity,
                selectPaths: options.availableColumns
            });

            // In the json, use "/" to separate navigation properties not ".", except when you're writing code for the kendo column template.
            _.forEach(options.columnOverrides, function (column) {
                column.selectPath = column.field;
                column.field = column.field.replaceAll("/", ".");
            });

            options.gridColumns = gridColumns.build({
                entityName: options.entity,
                expand: options.expand,
                availableColumns: options.availableColumns,
                columnOrder: options.columnOrder,
                columnOverrides: options.columnOverrides,
                stickyDefaultName: options.stickyDefaultName,
                utilityColumns: options.utilityColumns,
                columnRenames: options.columnRenames,
                displayFirstItemInCollections: options.displayFirstItemInCollections
            });

            if (options.kendoFilter) {
                // Apply zFiltered to fields that are being filtered by Code Ids
                const filterFields = me._getFieldsFromKendoFilter(options.kendoFilter);
                _.forEach(options.gridColumns, gridColumn => {
                    const { codeSelectPath } = gridColumn;
                    if (codeSelectPath && _.includes(filterFields, codeSelectPath)) {
                        gridColumn.zFiltered = true;
                    }
                });
            }

            if (options.defaultColumnWidth) {
                options.gridColumns = options.gridColumns.map(column => {
                    return {
                        ...column,
                        width: column.width === undefined ? options.defaultColumnWidth : column.width
                    };
                });
            }
        }

        options.staticDataSource = options.staticDataSource === undefined
            ? false
            : options.staticDataSource;

        options.pageSize = options.pageSize
            || (options.dataSource
                && me.options.staticDataSource
                && options.dataSource.pageSize())
            || 10;
        options.kendoSort = options.kendoSort
            || (options.dataSource
                && me.options.staticDataSource
                && options.dataSource.sort())
            || [];

        var field = _.isString(options.stickyDefaultName)
            ? options.stickyDefaultName
            : null;
        if (options.widgetId && !field) {
            field = options.widgetId;
        }
        var stickyDefaults = getGridStickyDefaults({
            EntityName: options.entity,
            Field: field
        }).Value || {};
        if (stickyDefaults.PageSize) {
            options.pageSize = stickyDefaults.PageSize;
        }
        if (stickyDefaults.Sort) {
            options.kendoSort = stickyDefaults.Sort.filter((sort) => !sort.field.startsWith("UDF_") && !sort.field.startsWith("UDFLV_"));
        }

        me._autoSelect = options.autoSelect === undefined
            ? true
            : options.autoSelect;

        me._fireChangeOnReselect = options.fireChangeOnReselect === undefined
            ? false
            : options.fireChangeOnReselect;
        me._fireChangeOnReselectIgnoreNull = options.fireChangeOnReselectIgnoreNull === undefined
            ? false
            : options.fireChangeOnReselectIgnoreNull;
        me._previousPageSize = options.pageSize;
        me._previousSort = options.kendoSort;

        // From kendo source code for the columnShow event:
        // column = grep(columns, function (column) {
        //     return column.field == field || column.title == field;
        // })[0];
        // When you click the column menu to show another column it looks for either the field or the title to determine which column to show
        // We have fields like BillTo.ShipTo.City and City both with the title of City and depending on column order it can show or hide the wrong column
        // This means we can NEVER have a title with the same value as any field
        _.each(options.gridColumns, function (column) {
            if (column.title) {
                column.title = column.title + " ";
            }
        });

        if (options.allowExcelExport == undefined) {
            options.allowExcelExport = true;
        }

        options.selectable = options.selectable == undefined
            ? "row"
            : options.selectable;

        options.clearAllFiltersOption = options.clearAllFiltersOption === undefined
            ? false
            : options.clearAllFiltersOption;

        options.filterable = options.filterable != undefined
            ? me.options.filterable
            : me._filterable();

        if (me.options.dataSource && options.staticDataSource) {
            me.options.dataSource.pageSize(me.options.pageSize);
            me.options.dataSource.sort(me.options.kendoSort);
        }

        options.isUserGridTab = options.isUserGridTab === undefined
            ? false
            : options.isUserGridTab;

        options.allowColumnSelection = options.allowColumnSelection === undefined
            ? true
            : options.allowColumnSelection;

        options.persistSelection = options.persistSelection === undefined
            ? false
            : options.persistSelection;
    }

    build(options?: GridOptions) {
        var me = this;
        if (localStorage.getItem("page-load-debugging")) {
            console.time(`Grid - ${me.options.stickyDefaultName}`);
        }

        if (options) {
            me.setOptions({
                ...options,
                rightLocked: options.requiredRight
                    ? !userHasRight(options.requiredRight)
                    : false
            });
        }

        me._onChange = [];
        me._onDoubleClick = [];
        me._onDelete = [];
        me._onEdit = [];
        me._onDataBound = [];

        me._renderSearchOptions();

        if (me.options.dataBound) {
            me.onDataBound(me.options.dataBound);
        }

        if (me.options.change) {
            me.onChange(me.options.change);
        }

        // http://docs.telerik.com/kendo-ui/api/javascript/ui/grid
        var config: kendo.ui.GridOptions = {
            autoBind: me.options.autoBind,
            change: me._change(),
            columnHide: me._columnHide(me.options.columnHide),
            columnMenu: me._columnMenu(),
            columnMenuInit: me._columnMenuInit(),
            columnMenuOpen: me._columnMenuOpen(),
            columns: me.options.gridColumns,
            columnReorder: me.options.columnReorder || me._columnReorder(),
            columnResize: me._columnResize(),
            columnShow: me._columnShow(me.options.columnShow),
            dataBound: me._dataBound(),
            dataSource: me.options.dataSource || me._dataSource(),
            filterable: me.options.filterable,
            height: me.options.height,
            navigatable: true,
            pageable: me.options.pageable == undefined
                ? me._pageable()
                : me.options.pageable,
            reorderable: true,
            resizable: true,
            selectable: me.options.selectable == undefined
                ? "row"
                : me.options.selectable,
            sortable: me.options.sortable == undefined
                ? me._sortable()
                : me.options.sortable,
            noRecords: me._noRecordsToDisplay(),
            detailInit: me.options.detailInit,
            detailTemplate: me.options.detailTemplate,
            persistSelection: me.options.persistSelection
        };

        delete me.options.zScreen;

        var selector = me.options.gridObject || $(`#${me.id}`);

        me.kendoGrid = selector.kendoGrid(config).data("kendoGrid");

        // Make the grid jump to page 1 whenever it is sorted
        me.kendoGrid.bind("sort", function (e) {
            gridUtil.firstPage(me.kendoGrid.dataSource);
        });

        if (me.options.allowExcelExport) {
            var printId = `${me.options.id}PrintButton`,
                printButton = $("<a id='" + printId + "' class='k-grid-excelExport k-link' title='Print'><span class='grid-print k-i-excel k-icon'></span></a>");

            if (selector.find(".k-pager-refresh").length == 0) {
                printButton.insertBefore(selector.find(".k-pager-info"));
            } else {
                printButton.insertAfter(selector.find(".k-pager-refresh"));
            }
            printButton.tie("click", function () {
                zKendo.setExprAlwaysSafe();
                selector.mask();
                me.exportToExcel(selector.data("kendoGrid"));
            });
        }

        me._recalculateMinimumGridWidth();
        me._handleRowDoubleClick();
        me._handleEditButtonClick();
        me._handleDeleteButtonClick();
        me._handleViewSignatureButtonClick();
        me._handleViewURLButtonClick();
        me._hideColumnMenus();

        if (localStorage.getItem("page-load-debugging")) {
            console.timeEnd(`Grid - ${me.options.stickyDefaultName}`);
        }
    }

    exportToExcel(kendoGrid: kendo.ui.Grid) {
        var me = this;

        var exportDataToExcel = (data: any[]) => {
            let columns = _.filter(kendoGrid.columns, (column) => !column.hidden && (column as any).id != "EditDelete" && (column as any).field != "DestinationType"),
                templates = _.map(columns, column => {
                    if (column.template && column.field !== 'NoteTypeId') {
                        return _.isFunction(column.template)
                            ? column.template
                            : kendo.template(column.template as string);
                    } else {
                        return (e: any) => {
                            let result = _.get(e, column.field.replaceAll("/", "."));
                            if (result === null || result === undefined || result === "") {
                                return "---";
                            } else if (column.field === 'CreatedUser') {
                                return `${result.FirstName} ${result.LastName}`;
                            } else if (column.field === 'NoteTypeId') {
                                for(let i = 0; i < codes.NoteType.length; i++) {
                                    if (codes.NoteType[i].Id === result) {
                                        return `${codes.NoteType[i].CodeNumber}`;
                                    }
                                }
                            }
                            return `${result}`;
                        };
                    }
                }),
                rows = [],
                firstRow = { cells: [] },
                sheetColumns = [];

            _.forEach(columns, column => {
                firstRow.cells.push({ value: column.title, background: "#7A7A7A", color: "#FFFFFF" });
            });
            rows.push(firstRow);

            for (let i = 0; i < data.length; i++) {
                let tempRow = { cells: [] };
                _.forEach(templates, template => {
                    tempRow.cells.push({ value: $($.parseHTML(template(data[i]))).text().trim() });
                });
                rows.push(tempRow);
                delete data[i];
            }

            for (let i = 0; i < columns.length; i++) {
                sheetColumns.push({ autoWidth: true });
            }

            var workbook = new kendo.ooxml.Workbook({
                sheets: [
                    {
                        columns: sheetColumns,
                        rows: rows,
                        frozenRows: 1
                    }
                ]
            });

            workbook.toDataURLAsync().then(function (dataURL) {
                kendo.saveAs({
                    dataURI: dataURL,
                    fileName: `Export.xlsx`
                });
                $(kendoGrid.element).unmask();
            });
        };

        let parameterMap = me.parameterMap,
            entity = me.options.entity;

        if (kendoGrid.dataSource.options.transport) {
            parameterMap = parameterMap
                || kendoGrid.dataSource.transport.parameterMap({
                    filter: kendoGrid.dataSource.filter(),
                    sort: kendoGrid.dataSource.sort()
                }, "");
            var read: any = kendoGrid.dataSource.options.transport.read;
            entity = read
                ? (typeof (read.url) === "function"
                    ? read.url()
                    : read.url)
                : entity;
        }

        if (parameterMap) {
            getAll({
                entity: entity,
                filter: parameterMap.$filter,
                expand: parameterMap.$expand,
                select: parameterMap.$select,
                orderBy: parameterMap.$orderby
            }).done((results) => {
                exportDataToExcel(results);
            });
        } else {
            let kendoFilter = kendoGrid.dataSource.filter(),
                kendoSort = kendoGrid.dataSource.sort(),
                data = deepClone(kendoGrid.dataSource.data()),
                process = _.flow([
                    d => util.filter(d, kendoFilter),
                    d => util.sort(d, kendoSort),
                    d => exportDataToExcel(d)
                ]);

            process(data);
        }
    }

    refresh() {
        let me = this,
            element = me.kendoGrid && me.kendoGrid.element;

        var refreshButton = $(element).find(".k-i-reload");
        if (refreshButton.length > 0) {
            $(refreshButton).click();
        } else {
            me.kendoGrid.dataSource.read();
        }
    }

    lastPage() {
        var me = this;

        return gridUtil.lastPage(me.kendoGrid.dataSource);
    }

    mask(message?: string) {
        var me = this;
        message = message || "";

        me.kendoGrid.content.append(maskUtil.newLoadingMask({
            instructions: ll((message || "LoadingGridData"), true)
        }));

        me.kendoGrid.content.stop();
        me.kendoGrid.content.scrollTop(0);
        me.kendoGrid.content.css({
            "overflow": "hidden",
        });
    }

    unmask() {
        var me = this;

        me.kendoGrid.content.find(".grid-instructions-container").remove();
        me.kendoGrid.content.css({
            "overflow": "auto scroll"
        });
    }

    setFilter(newFilter) {
        var me = this;

        me.options.filter = newFilter;
    }

    setUrl(url) {
        let me = this;

        me.options.url = url;
        me.kendoGrid.setDataSource(me._dataSource());
    }

    onChange(action: (e: { selectedItem: kendo.data.TypedObservableObject<T> }) => void) {
        var me = this;

        me._onChange.push(action);
    }

    onDelete(action: (e: { item: kendo.data.TypedObservableObject<T>, preventDefault: () => void }) => void) {
        var me = this;

        me._onDelete.push(action);
    }

    onEdit(action: (e: { item: kendo.data.TypedObservableObject<T>, preventDefault: () => void }) => void) {
        var me = this;

        me._onEdit.push(action);
    }

    onDoubleClick(action: (e: { item: kendo.data.TypedObservableObject<T>, preventDefault: () => void }) => void) {
        var me = this;

        me._onDoubleClick.push(action);
    }

    onDataBound(action: (e: { selectedItem: kendo.data.TypedObservableObject<T>, sender: { _data: any, tbody: any } }) => void) {
        var me = this;

        me._onDataBound.push(action);
    }

    setCanDelete(predicate: (data: any) => boolean) {
        var me = this;

        me._canDelete = predicate;
    }

    showColumn(field: EntityFields<T>) {
        var me = this;

        me.kendoGrid.showColumn(field);
    }

    hideColumn(field: EntityFields<T>) {
        var me = this;

        me.kendoGrid.hideColumn(field);
    }

    cleanSearchContainer() {
        var me = this;

        $(`#${me.options.searchContainerId} > :not(#${me.options.searchContainerId}_Actions)`).remove();
        $(`#${me.options.searchContainerId}_Actions`).empty();
        $(`.k-popup[id^=${me.options.id}]`).remove();
    }

    pauseStickyDefaults() {
        this.dontUpdateStickyDefaults = true;
    }

    unpauseStickyDefaults() {
        this.dontUpdateStickyDefaults = false;
    }

    clearFilters() {
        var me = this;

        _.forEach(me.customFilterMenus, (filterMenu) => {
            filterMenu.clearAllInputs();
        });
        _.forEach(me.kendoGrid.columns, (column: any) => {
            column.zFiltered = false;
        });
        me.kendoGrid.dataSource.filter({});
    }

    _renderSearchOptions() {
        var me = this,
            options = me.options;

        options.searchOptions = options.searchOptions || [];

        options.divisions = options.divisions === undefined
            ? options.searchType.divisions
            : options.divisions;

        options.regions = options.regions === undefined
            ? options.searchType.regions
            : options.regions;

        if ((options.divisions || options.regions) && user && user.companyData && user.companyData.DivisionAndRegionEnabled) {

            options.divisionsNavigationTypes = options.divisionsNavigationTypes === undefined
                ? options.searchType.divisionsNavigationTypes
                : options.divisionsNavigationTypes;

            options.regionsNavigationTypes = options.regionsNavigationTypes === undefined
                ? options.searchType.regionsNavigationTypes
                : options.regionsNavigationTypes;

            options.divisionsFilterOverride = options.divisionsFilterOverride === undefined
                ? options.searchType.divisionsFilterOverride
                : options.divisionsFilterOverride;

            options.regionsFilterOverride = options.regionsFilterOverride === undefined
                ? options.searchType.regionsFilterOverride
                : options.regionsFilterOverride;

            options.regionNotRequired = options.regionNotRequired === undefined
                ? options.searchType.regionNotRequired
                : options.regionNotRequired;

            options.divisionsAndRegionsAndMyStuff = options.divisionsAndRegionsAndMyStuff === undefined
                ? options.searchType.divisionsAndRegionsAndMyStuff
                : options.divisionsAndRegionsAndMyStuff;

            options.defaultDivisionSearchOption = options.defaultDivisionSearchOption === undefined
                ? options.searchType.defaultDivisionSearchOption
                : options.defaultDivisionSearchOption;

            options.defaultRegionSearchOption = options.defaultRegionSearchOption === undefined
                ? options.searchType.defaultRegionSearchOption
                : options.defaultRegionSearchOption;

            var divAndRegOptions = divisionRegion.createDivisionAndRegionSearchOptions(options);

            if (options.divisions) {
                options.searchOptions.push(divAndRegOptions.division);
            }

            if (options.regions) {
                if (!options.isSearchPopUp) {
                    divAndRegOptions.region.showSearchPopUp = true;
                }
                options.searchOptions.push(divAndRegOptions.region);
            }
        }

        if (localStorage.defaultCompanyGuid) {
            options.searchOptions = _.map(options.searchOptions, (searchOption) => {
                if (typeof searchOption === "string") {
                    return { field: searchOption, type: "TextBox" };
                } else {
                    return searchOption;
                }
            });
        }

        searchOptions.initialize({
            options: options,
            _id: options.id,
            pushColumns: options.pushColumns,
            columnOrder: options.columnOrder
        });

        if (options.searchOptions.length > 0) {
            searchOptions.render({ options: options, _id: options.id });
        } else {
            $(`#${options.id}_SearchContainer`).css("min-height", "0").css("padding-top", 0);
        }
    }

    _hideSearchContainer() {
        var me = this;

        $(`#${me.id}_SearchContainer`).css("min-height", "0");
    }

    _renderSearchButtons() {
        var me = this;

        var filterButton = new Button({
            id: `${me.id}_filterDataButton`,
            classes: "filter-data-button",
            label: ll("Search")
        });

        var clearSearch = new Button({
            id: `${me.id}_clearfiltersButton`,
            label: ll("Clear")
        });

        filterButton.render(`#${me.id}_SearchContainer_Actions`);
        clearSearch.render(`#${me.id}_SearchContainer_Actions`);

        $(`#${me.id}_filterDataButton`).on("click", function () {
            me._refilterSearchOptions();
        });

        $(`#${me.id}_clearfiltersButton`).on("click", function () {
            me._clearSearchOptions();
        });

        if (me.options.liveFilter) {
            $(`#${me.id}_filterDataButton`).hide();
            $(`#${me.id}_SearchContainer`).css("min-height", 0);
        }
    }

    _clearSearchOptions() {
        var me = this;

        var searchInputs = $(`#${me.id}_SearchContainer`).find(".search-input");

        _.forEach(searchInputs, function (searchInput) {
            $(searchInput).val("");
            var kendoComboBox = $(searchInput).data("kendoComboBox");
            if (kendoComboBox) {
                kendoComboBox.wrapper.siblings(".pane-field-number").text("");
            }
        });

        me._refilterSearchOptions({ clear: true });
    }

    _refilterSearchOptions(args?) {
        var me = this,
            options = me.options;

        args = args || {};

        var searchInputElements = $(`#${me.id}_SearchContainer`).find(".search-input"),
            searchInputs = _.filter(searchInputElements, function (i) {
                return i.id && i.id.indexOf("_CB_") == -1;
            }),
            filters = [];

        _.forEach(searchInputs, function (searchInput) {
            var searchOption = $(searchInput).data("searchOption"),
                value = $(searchInput).val().trim(),
                role = $(searchInput).data("role"),
                isComboBox = role && role.indexOf("combobox") != -1;

            if (!value) {
                return;
            }

            if (isComboBox) {
                if (value == -1) {
                    return;
                }
                filters.push(filter.equals(searchOption, parseInt(value, 10)));
            } else {
                filters.push(filter.startsWith(searchOption, value));
            }
        });
        options.searchOptionsFilter = filter.all(filters);

        if (args.clear) {
            me.kendoGrid.dataSource.filter([]);
        } else {
            me.kendoGrid.dataSource.read();
        }
    }

    _change() {
        var me = this;

        return function (e) {
            var grid = this,
                row = grid.select().closest("tr"),
                dataItem = grid.dataItem(row);

            me._setSelectedItem(e, dataItem);
        };
    }

    _columnHide(additionalFn) {
        var me = this;

        return function () {
            if (additionalFn) {
                additionalFn(me);
            }
            me._recalculateMinimumGridWidth();
            me._saveStickyDefaults();
        };
    }

    _columnMenu() {
        var me = this,
            columnMenu = {
                columns: me.options.allowColumnSelection !== false,
                messages: {
                    columns: ll("ColumnMenuColumns"),
                    filter: ll("ColumnMenuFilter"),
                    sortAscending: ll("ColumnMenuSortAscending"),
                    sortDescending: ll("ColumnMenuSortDescending")
                }
        };

        return columnMenu;
    }

    _columnMenuInit() {
        var me = this;

        return function (e) {
            var entityName = me.options.entity;

            var codeFields = _(me.options.gridColumns)
                .filter(function (column) { return column.isCode; })
                .map(function (column: any) { return column.field; })
                .value();

            var udfFields = _(me.options.gridColumns)
                .filter(function (column) { return column.isUDF; })
                .map(function (column: any) { return column.field; })
                .value();

            var udflvFields = _(me.options.gridColumns)
                .filter(function (column) { return column.isUDFLV; })
                .map(function (column: any) { return column.field; })
                .value();

            var utilityFields = _(me.options.gridColumns)
                .filter(function (column) { return column.isUtility; })
                .map(function (column: any) { return column.field; })
                .value();

            var kendoMenu = e.container.find(".k-menu").data("kendoMenu");
            var chooseColumnsMenuItem = $(kendoMenu.element).find(".k-columns-item");

            if (areGridsEditable()) {
                var menuGroupElement = chooseColumnsMenuItem.find(".k-menu-group");
                menuGroupElement.addClass("choose-column-container");
                var menuItems = menuGroupElement.children();
                var groupedMenuItems = {};

                _.forEach(menuItems, function (menuItem) {
                    $(menuItem).removeClass("k-first");
                    $(menuItem).removeClass("k-last");

                    var field = $(menuItem).find("input").data("field");
                    var findColumn = (columns: any[]) => _.find(columns, (item) => {
                        if (item.columns) {
                            return findColumn(item.columns);
                        } else {
                            let fieldCompare = field.replaceAll("/", "").replaceAll(".", ""),
                                itemFieldCompare = item.field.replaceAll("/", "").replaceAll(".", "");
                            return fieldCompare == itemFieldCompare;
                        }
                    });
                    var column = findColumn(me.options.gridColumns);
                    var fullExpandPath;

                    if (_.includes(utilityFields, field) || _.includes(udfFields, field)) {
                        fullExpandPath = schema.splitLast(entityName + "/" + field.replaceAll(".", "/"))[0];
                    } else if (_.includes(udflvFields, field)) {
                        fullExpandPath = schema.splitLast(entityName + "/" + field.replaceAll(".", "/"))[0];
                        fullExpandPath = schema.splitLast(fullExpandPath)[0];
                    } else {
                        try {
                            var propertyInfo = schema.getPropertyInfo({ entityName: entityName, path: field });
                            fullExpandPath = propertyInfo.fullExpandPath;
                        } catch (e) {
                            fullExpandPath = schema.splitLast(`${entityName}/${field}`)[0];

                            if (fullExpandPath == `${entityName}/${entityName}`) {
                                fullExpandPath = entityName;
                            }
                        }

                        if (_.find(codeFields, function (codeField) { return codeField == field; })) {
                            fullExpandPath = schema.splitLast(fullExpandPath)[0];
                        }
                    }

                    if (column.menuGroup) {
                        fullExpandPath = `${entityName}/${column.menuGroup}`;
                    }

                    groupedMenuItems[fullExpandPath] = groupedMenuItems[fullExpandPath] || [];
                    groupedMenuItems[fullExpandPath].push(menuItem);
                });

                var sortedExpandPaths = _(groupedMenuItems)
                    .keys()
                    .without(entityName)
                    .sortBy()
                    .value();

                sortedExpandPaths.unshift(entityName);

                menuGroupElement.empty();
                _.forEach(sortedExpandPaths, function (expandPath) {
                    if (!groupedMenuItems[expandPath] || groupedMenuItems[expandPath].length == 0) {
                        return;
                    }

                    var headerText;

                    if (expandPath == entityName) {
                        try {
                            headerText = schema.localizeEntity({ entityName: entityName });
                        } catch (e) {
                            headerText = ll(entityName);
                        }
                    } else {
                        try {
                            var navigationPropertyInfo = schema.getNavigationPropertyInfo({ path: expandPath });
                            headerText = schema.localizeNavigationProperty({ entityName: navigationPropertyInfo.parentEntityName, navigationPropertyName: navigationPropertyInfo.navigationPropertyName });
                        } catch (e) {
                            headerText = schema.splitFirst(expandPath)[0];
                        }
                    }

                    var span = $("<span class='z-grid-menu-dropdown-heading'></span>");
                    span.text(headerText);

                    var li = $("<li></li>");
                    li.append(span);

                    menuGroupElement.append(li);

                    var sortedMenuItems = _.sortBy(groupedMenuItems[expandPath], function (menuItem) {
                        return $(menuItem).text().toLowerCase();
                    });

                    menuGroupElement.append(sortedMenuItems);
                });
            } else {
                chooseColumnsMenuItem.disable();
                chooseColumnsMenuItem.find(".k-menu-expand-arrow").remove();

                controls.toolTip({
                    content: ll("DisabledChooseColumnsTooltip"),
                    alignX: "left",
                    alignY: "center",
                    alignTo: "target",
                    offsetX: 5,
                    selector: chooseColumnsMenuItem
                });
            }

            var foundColumn = _.find(me.options.gridColumns, function (element: any) {
                if (element.field == e.field) {
                    return true;
                }
                return false;
            });

            if (me.options.filterable) {
                var filterMenu = new GridFilterMenu({
                    menu: kendoMenu,
                    column: foundColumn,
                    field: e.field,
                    gridId: me.options.id,
                    mainEntityType: me.options.entity,
                    menuContainer: e.container.find("ul"),
                    customFilterMenu: foundColumn.customFilterMenu
                });

                if (filterMenu.create()) {
                    me.customFilterMenus.push(filterMenu);
                }

                fixGridFilterMenuForChrome(kendoMenu);
            }
        };
    }

    _columnReorder() {
        var me = this;

        return function () {
            // columns aren't actually reordered yet when the event fires
            setTimeout(function () {
                me._saveStickyDefaults();
            }, 10);
        };
    }

    _columnResize() {
        var me = this;

        return function () {
            me._recalculateMinimumGridWidth();
            me._saveStickyDefaults();
        };
    }

    _columnShow(additionalFn?) {
        var me = this;

        return function () {
            if (additionalFn) {
                additionalFn(me);
            } else {
                me.refresh();
            }
            me._recalculateMinimumGridWidth();
            me._saveStickyDefaults();
        };
    }

    _dataBound() {
        var me = this;

        return function (e) {
            var grid = this;

            if (me.options.selectable) {
                gridUtil.selectPrevious({ observable: me.selectedItem, grid: grid, e: e, autoSelect: me._autoSelect });
            }

            if ((!me._autoSelect && !me.selectedItem) || grid.dataSource.total() == 0) {
                me._setSelectedItem(e, null);
            } else {
                let row = grid.select().closest("tr");
                var dataItem = grid.dataItem(row);
                me._setSelectedItem(e, dataItem);
            }

            if (grid.dataSource.page() != 1 && grid.dataSource.page() > grid.dataSource.totalPages()) {
                grid.dataSource.page(1);
            }

            if (me._canDelete) {
                _.forEach(e.sender._data, function (data) {
                    var uid = data.uid;
                    let row = grid.tbody.find(`tr[data-uid="${uid}"]`);

                    if (!me._canDelete(data)) {
                        row.find(".k-grid-delete").remove();
                    }
                });
            }

            _.forEach(me._onDataBound, function (action) {
                e.selectedItem = me.selectedItem;
                action.bind(grid)(e);
            });

            // Save sticky defaults, if needed.

            var pageSize = grid.dataSource.pageSize(),
                sort = grid.dataSource.sort(),
                pageSizeChanged = pageSize != me._previousPageSize,
                sortChanged = !_.isEqual(sort, me._previousSort);

            if (me.kendoGrid && (pageSizeChanged || sortChanged)) {
                me._saveStickyDefaults();
            }

            me._previousPageSize = pageSize;
            me._previousSort = sort;

            $.each(grid.columns, function (index, column) {
                if (column.zHeaderElement && !column.zHeaderElement.addClass) {
                    column.zHeaderElement = null;
                    column.zFiltered = null;
                }
                if (!column.zHeaderElement && grid && grid.thead) {
                    column.zHeaderElement = grid.thead.find(`.k-link:contains(${column.title})`)
                        .parents("th.k-header")
                        .find(".k-header-column-menu");
                }

                if (column.hideColumnMenu) {
                    column.zHeaderElement.hide();
                }

                if (column.zFiltered) {
                    $.wait(100).then(function () {
                        column.zHeaderElement.addClass("k-state-active");
                    });
                }
            });

            grid.tbody.find('a[href="#"]').removeAttr("href");
            grid.content.css({ "overflow-x": "auto", "overflow-y": "scroll" });
        };
    }

    _dataSource() {
        var me = this;

        const read: kendo.data.DataSourceTransportRead = {
            contentType: "application/json",
            dataType: "json",
            url: (me.options.urlPrefix || "") + (me.options.url || me.options.entity) + (me.options.urlPostfix || ""),
            beforeSend: me.options.beforeSend || emptyFn
        };

        if (me.options.companyGuid) {
            read.headers = {
                "Company-GUID": me.options.companyGuid
            };
        }

        const transport: kendo.data.DataSourceTransport = {
            parameterMap: function (data, type) {
                return me._parameterMap(data, type);
            },
            read
        };

        // http://docs.telerik.com/kendo-ui/api/javascript/data/datasource
        var ds = dataSource({
            filter: me.options.kendoFilter,
            pageSize: me.options.pageSize,
            serverFiltering: true,
            serverPaging: true,
            serverSorting: true,
            sort: me.options.kendoSort,
            transport,
            type: "odata",
            parent: $(`#${this.id}`)
        });

        return ds;
    }

    _parameterMap(data, type) {
        var me = this,
            columns = me.kendoGrid
                ? me.kendoGrid.getOptions().columns
                : me.options.gridColumns,
            expand = [...me.options.expand, ...(me.options.alwaysExpand || [])],
            selects;

        selects = _(columns)
            .filter(function (c) {
                return c.field && !c.hidden && !c.isUtility;
            })
            .map(function (c) {
                return c.codeSelectPath || c.selectPath;
            })
            .compact()
            .value();

        selects = odata.unionSelects({ selects: [selects, me.options.alwaysSelect], expand: expand });

        var filt = filter.empty();

        if (me.options.filter) {
            if (_.isFunction(me.options.filter)) {
                filt = filt.and(me.options.filter());
            } else {
                filt = filt.and(me.options.filter);
            }
        }

        if (me.options.searchOptionsFilter) {
            filt = filt.and(me.options.searchOptionsFilter);
        }

        me.parameterMap = buildODataKendoParams({
            data: data,
            selectExpression: selects.join(","),
            expandExpression: expand.join(","),
            filterExpression: filt.toString(),
            sortExpression: me.options.sort
        });

        return me.parameterMap;
    }

    _setSelectedItem(e, selectedItem) {
        let me = this,
            oldSelectedId = me.selectedItem && me.selectedItem.Id,
            newSelectedId = selectedItem && selectedItem.Id;

        me.selectedItem = selectedItem;

        if (oldSelectedId != newSelectedId || (me._fireChangeOnReselect && newSelectedId) || me._fireChangeOnReselectIgnoreNull) {
            _.forEach(me._onChange, function (action) {
                e.selectedItem = me.selectedItem;
                action.bind(me.kendoGrid)(e);
            });
        }
    }

    _filterable() {
        var filterable = {
            messages: {
                and: ll("FilterableAnd"),
                clear: ll("FilterableClear"),
                filter: ll("FilterableFilter"),
                info: ll("FilterableInfo"),
                isFalse: ll("FilterableIsFalse"),
                isTrue: ll("FilterableIsTrue"),
                or: ll("FilterableOr"),
                selectValue: ll("FilterableSelectValue"),
                cancel: ll("FilterableCancel"),
                operator: ll("FilterableOperator"),
                value: ll("FilterableValue")
            },
            operators: {
                string: {
                    startswith: ll("FilterableStartswith"),
                    contains: ll("FilterableContains"),
                    doesnotcontain: ll("FilterableDoesnotcontain"),
                    endswith: ll("FilterableEndswith"),
                    eq: ll("FilterableEq"),
                    neq: ll("FilterableNeq"),
                    isempty: ll("IsBlank"),
                    isnotempty: ll("IsNotBlank")
                }
            }
        };

        return filterable;
    }

    _pageable() {
        var me = this;

        var pageable = {
            buttonCount: 5,
            pageSizes: [5, 10, 15, 25, 50, 100],
            refresh: me.options.refresh === undefined ? true : me.options.refresh,
            messages: {
                display: ll("PageableDisplay"),
                empty: ll("PageableEmpty"),
                of: ll("PageableOf"),
                itemsPerPage: ll("PageableItemsPerPage"),
                first: ll("PageableFirst"),
                last: ll("PageableLast"),
                next: ll("PageableNext"),
                previous: ll("PageablePrevious"),
                refresh: ll("PageableRefresh"),
                morePages: ll("PageableMorePages")
            }
        };

        return pageable;
    }

    _sortable() {
        var sortable = {
            allowUnsort: true,
            mode: "single"
        };

        return sortable;
    }

    _handleRowDoubleClick() {
        var me = this;

        $(`#${me.id}`).tie("dblclick", "tbody tr", function () {
            _.forEach(me._onDoubleClick, function (action) {
                action({ item: me.selectedItem });
            });
            if (me.options.editOnDoubleClick) {
                _.forEach(me._onEdit, function (action) {
                    action({ item: me.selectedItem });
                });
            }
        });
    }

    _handleEditButtonClick() {
        var me = this;

        if (me.options.editButton) {
            $(`#${me.id}`).tie("click", me.options.rightLocked ? ".k-grid-search" : ".k-grid-edit", function (e) {
                e.preventDefault();
                _.forEach(me._onEdit, function (action) {
                    action({ item: me.selectedItem });
                });
            });
        }
    }

    _handleDeleteButtonClick() {
        var me = this;

        if (me.options.deleteButton) {
            if (typeof me.options.deleteButton == "object") {
                let deleteButton = me.options.deleteButton;
                $(`#${me.id}`).tie("click", ".k-grid-delete", function (e) {
                    e.preventDefault();

                    var item = me.selectedItem;
                    deleteEntity({
                        id: item.Id,
                        entity: me.options.entity,
                        checkDelete: deleteButton.checkDelete
                    }).done(function () {
                        me.refresh();

                        _.forEach(me._onDelete, function (action) {
                            action({ item: item });
                        });
                    });
                });
            } else {
                $(`#${me.id}`).tie("click", ".k-grid-delete", function (e) {
                    e.preventDefault();

                    var promises = [];
                    _.forEach(me._onDelete, function (action) {
                        promises.push(action({ item: me.selectedItem }));
                    });
                    $.whenAll(promises).done(function () {
                        me.refresh();
                    });
                });
            }
        }
    }

    _handleViewSignatureButtonClick() {
        var me = this;

        $(`#${me.id}`).tie("click", ".grid-signature-link", function (e) {
            var signatureId = me._getGridDataItemValue(e);
            if (signatureId) {
                var signaturePopup = new SignaturePopup();
                signaturePopup.popUp({ Id: signatureId });
            }
        });
    }

    _handleViewURLButtonClick() {
        var me = this;

        $(`#${me.id}`).tie("click", ".grid-url-link", function (e) {
            var value = me._getGridDataItemValue(e);
            if (value && typeof value === "string") {
                // label is before the pipe, url after
                var indexOfPipe = value.indexOf("|");
                var url = value.substring(indexOfPipe + 1);

                window.open(url, "_blank");
            }
        });
    }

    _getGridDataItemValue(e) {
        var grid = $(e.currentTarget).closest(".k-grid"),
            kendoGrid = $(grid).data("kendoGrid"),
            header = kendoGrid.thead,
            row = $(e.currentTarget).closest("tr"),
            cell = $(e.currentTarget).closest("td"),
            columnIndex = $("td", row).index(cell),
            columnField = header.find("th").eq(columnIndex).attr("data-field"),
            dataItem = kendoGrid.dataItem(row),
            value = _.get(dataItem, columnField);

        return value as any;
    }

    _hideColumnMenus() {
        var me = this;

        _.forEach(me.options.gridColumns, function (column) {
            if (column.field && column.hideColumnMenu) {
                $("#{0} [data-field='{1}'] .k-header-column-menu".format(me.id, column.field)).hide();
            }
        });
    }

    _saveStickyDefaults() {
        var me = this;

        if (!me.options.stickyDefaultName) {
            return;
        }

        if (me.dontUpdateStickyDefaults) {
            return;
        }

        var options = me.kendoGrid.getOptions();
        var columns = options.columns;
        var pageSize = options.dataSource.pageSize;
        var sort = options.dataSource.sort;

        columns = _.map(columns, function (column: any) {
            return {
                field: column.field,
                hidden: column.hidden,
                width: column.width
            };
        });

        var field = me.options.stickyDefaultName;
        if (me.options.widgetId && !field) {
            field = me.options.widgetId;
        }
        setGridStickyDefaults({
            EntityName: me.options.entity,
            Field: field,
            Value: {
                Columns: columns,
                PageSize: pageSize,
                Sort: sort
            }
        });
    }

    _recalculateMinimumGridWidth() {
        var me = this;
        gridUtil.setGridMinimumWidth($(me.kendoGrid.element), me.kendoGrid.getOptions().columns);
    }

    _noRecordsToDisplay() {
        var noRecords = {
            template: "<div class='grid-instructions-container k-loading-mask'><div class='grid-instructions'  > #= z.ll(\"GridNoDataFound\", true) # </div></div>"
        };

        return noRecords;
    }

    _getGridIdFromOptions(options) {
        var gridId = options.id;

        if (!gridId && options.gridObject) {
            gridId = $(options.gridObject).attr("id");
        }

        if (options.zScreen && options.zScreen.id && gridId.indexOf("_") == -1) {
            gridId = `${options.zScreen.id}_${gridId}`;
        }

        options.id = gridId;

        return gridId;
    }

    _columnMenuOpen() {
        const me = this;

        return (e: kendo.ui.GridColumnMenuOpenEvent) => {
            if (me.options.filterable) {
                const filterMenu = $(e.container).find(".k-filter-menu:not(.custom-filter)");

                if (gridUtil.allowNullFilters(me.options.entity, e.field) && filterMenu.length > 0) {
                    _.forEach(filterMenu.find(`[data-role="dropdownlist"]:not(.k-filter-and)`), element => {
                        const dropDownList = $(element).data("kendoDropDownList"),
                            value = dropDownList.value(),
                            textBox = dropDownList.element.closest(".k-dropdown").next();

                        if (value === "isnull" ||
                            value === "isnotnull" ||
                            value === "isempty" ||
                            value === "isnotempty"
                        ) {
                            textBox.hide();
                        } else {
                            textBox.show();
                        }
                    });
                }
            }
        };
    }

    _getFieldsFromKendoFilter(kendoFilter: any): string[] {
        var me = this,
            fields = [];

        if (kendoFilter.filters) {
            _.forEach(kendoFilter.filters, childFilter => {
                fields.push(...me._getFieldsFromKendoFilter(childFilter));
            });
        } else {
            if (kendoFilter.field) {
                fields.push(kendoFilter.field);
            }
        }

        return fields;
    }
}

export function areGridsEditable() {
    return (localStorage.getItem("EditGrids") || "").toLowerCase() === "true" ||
        settings.brandName === "admin";
}
